import { Button, ProgressBar, Typography } from 'components';
import { POLL_TIME, PROCESSING_PERCENTAGE_FULL } from 'constants/constants';
import { FileTypeEnum } from 'gql/graphql';
import {
  CloseCircleIcon,
  FileDoneIcon,
  FileErrorIcon,
  FileProcessingIcon,
  RetryIcon
} from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { usePollFileData } from 'providers/FileProviders/usePollFileData';
import { FC, useCallback, useEffect, useState } from 'react';
import { cn } from 'utils/cn';

type StatusCardFile = 'processed' | 'processing' | 'error-in-type' | 'error-in-upload';

export interface IFileTM {
  id: string;
  uid?: string;
  status?: StatusCardFile;
  filename?: string;
  filesize?: string;
  percentage?: number;
  file?: File;
}
interface ICardFile extends IFileTM {
  onRemove?: (id: string) => void;
  onUpdate: (cardFile: IFileTM) => void;
  onRetry: (cardFile: IFileTM) => void;
}

const CardFile: FC<ICardFile> = ({
  id,
  uid,
  percentage,
  status,
  filename,
  filesize,
  file,
  onRemove,
  onUpdate,
  onRetry
}) => {
  const t = useTranslations();

  const [attempt, setAttempt] = useState(0);

  const isError = status?.includes('error');
  const isErrorInType = status === 'error-in-type';
  const isErrorInUpload = status === 'error-in-upload';
  const isProcess = status?.includes('process');
  const isProcessing = status === 'processing';
  const isProcessed = status === 'processed';
  const showProgressBar = isProcess && !Number.isNaN(Number(percentage));

  const {
    data: fileData,
    isFetched,
    refetch: pollFileData
  } = usePollFileData(
    {
      uids: [uid as string],
      fileType: FileTypeEnum.TranslationMemoryFile
    },
    {
      enabled: !!uid,
      staleTime: 0,
      refetchInterval: !!uid && attempt < 3 && status !== 'processed' ? POLL_TIME : false
    }
  );

  const statusAndfileData = fileData?.at(0);

  const reset = useCallback(() => setAttempt(0), []);

  const poolProcessing = useCallback(async () => {
    try {
      if (uid && attempt < 3 && isFetched) {
        if (statusAndfileData?.hasErrors) {
          throw onUpdate({
            id,
            status: 'error-in-upload'
          });
        }

        if (statusAndfileData?.progress !== PROCESSING_PERCENTAGE_FULL) {
          onUpdate({
            id,
            percentage: statusAndfileData?.progress,
            status: 'processing'
          });
          return pollFileData();
        }

        if (statusAndfileData?.progress === PROCESSING_PERCENTAGE_FULL) {
          reset();
          return onUpdate({
            id,
            percentage: statusAndfileData?.progress,
            status: 'processed'
          });
        }
      }
    } catch (error) {
      if (attempt >= 3) {
        return onUpdate({
          id,
          percentage: 0,
          status: 'error-in-upload'
        });
      }
      return setAttempt((oldState) => oldState + 1);
    }
  }, [
    id,
    uid,
    attempt,
    isFetched,
    statusAndfileData?.hasErrors,
    statusAndfileData?.progress,
    reset,
    onUpdate,
    pollFileData
  ]);

  const handleRemove = () => onRemove?.(id);

  const handleRetry = () => {
    onRetry({
      id,
      file,
      uid: undefined,
      status: 'processing',
      percentage: 0,
      filename: filename,
      filesize: filesize
    });
    reset();
  };

  useEffect(() => {
    poolProcessing();
  }, [poolProcessing]);

  return (
    <div
      className={cn(
        'relative flex rounded-[8px] border border-tertiary-600 p-4',
        isErrorInUpload ? 'pb-2' : ''
      )}
      id={id}
    >
      <div className="absolute right-2 top-2 z-[1000]">
        <Button
          size="small"
          variant="icon"
          color="neutral"
          className="!w-8 rounded-full"
          onClick={handleRemove}
          startAdornment={<CloseCircleIcon className="scale-75" />}
        />
      </div>
      <div className="pr-4">
        {isError && <FileErrorIcon className="pl-[2px]" />}
        {isProcessing && <FileProcessingIcon />}
        {isProcessed && <FileDoneIcon className="pl-[2px]" />}
      </div>
      <div className="flex flex-1 flex-col">
        {isError && (
          <Typography variant="light4" className="pb-[2px] !leading-4 text-secondary-red">
            {isErrorInType && (
              <>{t('translation_memory.modal_upload.card_file.error_invalid_type.title')}</>
            )}
            {isErrorInUpload && (
              <>{t('translation_memory.modal_upload.card_file.error_upload.title')}</>
            )}
          </Typography>
        )}
        <Typography variant="medium4" className="pb-[2px] !leading-5 text-tertiary-100">
          {filename}
        </Typography>
        {isProcess && (
          <Typography variant="light4" className="!leading-4 text-tertiary-200">
            {filesize}
          </Typography>
        )}
        {isError && (
          <Typography variant="light5" className="pt-2 text-tertiary-200">
            {isErrorInType && (
              <>{t('translation_memory.modal_upload.card_file.error_invalid_type.paragraph')}</>
            )}
            {isErrorInUpload && (
              <>{t('translation_memory.modal_upload.card_file.error_upload.paragraph')}</>
            )}
          </Typography>
        )}
        {isErrorInUpload && (
          <Button
            variant="icon"
            size="small"
            colorIcon="blue"
            startAdornment={<RetryIcon />}
            classNameTypography="text-secondary-link"
            label={t('translation_memory.modal_upload.card_file.retry')}
            onClick={handleRetry}
          />
        )}
        {showProgressBar && (
          <div className="mt-2 flex items-center">
            <ProgressBar
              hideNumber
              height="h-2"
              color="bg-primary-blue-100"
              percentage={percentage ?? 0}
              className="w-[90%]"
            />
            <Typography
              variant="light4"
              className="w-[10%] text-right !leading-3 text-tertiary-100"
            >
              {percentage}%
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardFile;
